/**
 * VuTwin friendship leveling + badge taxonomy.
 *
 * The XP/level layer is per-friendship in spirit, but for V1 it lives on the
 * game store as transient session state (see plan: Out of scope - per-pair
 * persistence is a follow-up). All math is pure / stateless so the upgrade
 * path is "swap the storage location, keep the calls."
 */

export type BadgeColor = "gold" | "hot" | "cyan";

export interface Badge {
  id: string;
  label: string;
  /** Single-character glyph rendered in the badge icon slot. */
  icon: string;
  color: BadgeColor;
}

export interface LevelInfo {
  level: number;
  /** Total XP threshold to ENTER this level. */
  xp: number;
  /** Display name shown next to the level pill. */
  title: string;
}

export const LEVELS: LevelInfo[] = [
  { level: 1, xp: 0, title: "STRANGERS" },
  { level: 2, xp: 60, title: "NEW PAIR" },
  { level: 3, xp: 140, title: "WARMING UP" },
  { level: 4, xp: 240, title: "IN SYNC" },
  { level: 5, xp: 360, title: "TIGHT" },
  { level: 6, xp: 500, title: "LOCKED IN" },
  { level: 7, xp: 660, title: "BFF" },
  { level: 8, xp: 840, title: "TWIN FLAME" },
  { level: 9, xp: 1040, title: "LEGENDARY" },
];

export interface LevelResolved {
  level: number;
  title: string;
  /** XP threshold of the CURRENT level (i.e. the floor). */
  xpForLevel: number;
  /** XP threshold of the NEXT level (cap at top). */
  xpForNext: number;
  /** Remaining XP until the next level. */
  xpToNext: number;
}

/** Resolve the level info for a given total XP. */
export function levelInfoFor(xp: number): LevelResolved {
  const safeXp = Math.max(0, Math.floor(xp));
  let curr = LEVELS[0]!;
  for (const lv of LEVELS) {
    if (safeXp >= lv.xp) curr = lv;
    else break;
  }
  const idx = LEVELS.findIndex((l) => l.level === curr.level);
  const next = LEVELS[idx + 1] ?? curr;
  const xpForNext = next.level === curr.level ? curr.xp : next.xp;
  return {
    level: curr.level,
    title: curr.title,
    xpForLevel: curr.xp,
    xpForNext,
    xpToNext: Math.max(0, xpForNext - safeXp),
  };
}

/**
 * XP earned by one shared pulse.
 *
 *   base        = round(syncPct)            // 1 XP per percentage point
 *   streakBonus = streak >= 7 ? 20 : streak >= 3 ? 10 : 0
 *   categoryBonus = MUSIC / CITIES / SHAPES feel weightier (+10)
 *   perfectBonus  = 100% → +25
 *   weekendBonus  = Sat/Sun (UTC) → +5
 */
export function computeXpThisPulse(input: {
  syncPct: number;
  categoryId: string;
  streak: number;
}): number {
  const { syncPct, categoryId, streak } = input;
  let xp = Math.round(Math.max(0, Math.min(100, syncPct)));
  if (streak >= 7) xp += 20;
  else if (streak >= 3) xp += 10;
  if (syncPct >= 100) xp += 25;
  if (["music", "cities", "shapes"].includes(categoryId)) xp += 10;
  const dow = new Date().getUTCDay();
  if (dow === 0 || dow === 6) xp += 5;
  return xp;
}

const BADGE = {
  PERFECT_TWIN: {
    id: "perfect",
    label: "PERFECT TWIN",
    icon: "★",
    color: "gold" as BadgeColor,
  },
  ELECTRIC: {
    id: "electric",
    label: "ELECTRIC",
    icon: "⚡",
    color: "gold" as BadgeColor,
  },
  STREAK_PLUS: {
    id: "streak+",
    label: "STREAK +1",
    icon: "◆",
    color: "gold" as BadgeColor,
  },
  DAILY_BONUS: {
    id: "daily",
    label: "DAILY BONUS",
    icon: "✦",
    color: "gold" as BadgeColor,
  },
  TIER_UP: {
    id: "tierup",
    label: "LEVEL UP",
    icon: "↑",
    color: "hot" as BadgeColor,
  },
};

const xInARow = (n: number): Badge => ({
  id: `${n}row`,
  label: `${n} IN A ROW`,
  icon: "♥",
  color: "hot",
});

const categoryMatch = (label: string): Badge => ({
  id: `${label.toLowerCase()}-match`,
  label: `${label.toUpperCase()} MATCH`,
  icon: "★",
  color: "cyan",
});

/**
 * Compute the badges earned for a given pulse outcome.
 * The result is intentionally non-deterministic in length (variable rewards
 * are the point) but capped at 3 to fit the badge-row UI.
 */
export function computeBadges(input: {
  syncPct: number;
  matches: boolean[];
  categoryLabel: string;
  streak: number;
  tieredUp: boolean;
}): Badge[] {
  const { syncPct, matches, categoryLabel, streak, tieredUp } = input;
  const out: Badge[] = [];

  if (syncPct >= 100) out.push(BADGE.PERFECT_TWIN);
  else if (syncPct >= 90) out.push(BADGE.ELECTRIC);

  // Longest run of consecutive matches
  let bestRun = 0;
  let cur = 0;
  for (const m of matches) {
    if (m) {
      cur += 1;
      if (cur > bestRun) bestRun = cur;
    } else cur = 0;
  }
  if (bestRun >= 3) out.push(xInARow(bestRun));
  else if (matches.filter(Boolean).length >= 3)
    out.push(categoryMatch(categoryLabel));

  if (tieredUp) out.push(BADGE.TIER_UP);
  else if (streak > 0 && streak % 7 === 0) out.push(BADGE.DAILY_BONUS);
  else if (streak > 0) out.push(BADGE.STREAK_PLUS);

  return out.slice(0, 3);
}
